import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowLeft, Sparkles, Zap, Crown, Building2, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth-context";

const PLAN_ICONS = {
  free: Sparkles,
  interview_ready_pass: Zap,
  career_pro: Crown,
  career_enterprise: Building2,
};

const CADENCE_LABEL = {
  forever: "Forever free",
  one_time: "One-time",
  monthly: "Billed monthly",
  from_monthly: "Custom pricing",
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [salesEmail, setSalesEmail] = useState("hello@intuiterview.com");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans);
        setSalesEmail(data.sales_email);
      } catch {
        setPlans([]);
      }
    })();
  }, []);

  const primaryTarget = user ? "/app/billing" : "/register";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-amber-500/[0.05] blur-3xl" />
        <div className="absolute top-40 left-0 h-80 w-80 rounded-full bg-amber-500/[0.03] blur-3xl" />
      </div>

      <header className="relative z-10 px-8 py-6 flex items-center justify-between border-b border-zinc-900">
        <BrandLogo variant="lockup" iconSize={30} testid="pricing-logo" />
        <Link
          to="/"
          data-testid="pricing-back-home"
          className="text-xs text-zinc-500 hover:text-zinc-200 uppercase tracking-widest inline-flex items-center gap-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-8 pt-16 pb-8 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-400 border border-amber-500/20 bg-amber-500/5 px-3 py-1.5 rounded-full mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" />
          Pricing
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-zinc-50 leading-tight">
          Everything You Need.
          <br />
          <span className="text-zinc-500">Free During Beta.</span>
        </h1>
        <p className="mt-5 text-sm text-zinc-500 max-w-xl mx-auto leading-relaxed">
          Try every Intuiterview plan and feature while we're in beta. No payment required.
        </p>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-8 pb-24">
        <div
          data-testid="pricing-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {plans.map((plan) => {
            const Icon = PLAN_ICONS[plan.id] || Sparkles;
            const highlight = plan.highlight;
            const isEnterprise = plan.id === "career_enterprise";
            return (
              <div
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                className={`relative flex flex-col p-6 rounded-2xl transition-all duration-300 ${
                  highlight
                    ? "border border-amber-500/40 bg-gradient-to-br from-amber-500/[0.06] via-zinc-900/40 to-zinc-950/60 shadow-[0_0_40px_-12px_rgba(245,158,11,0.4)] lg:-translate-y-2"
                    : "border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                }`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.24em] bg-amber-500 text-amber-950 rounded-full px-3 py-1 font-semibold">
                    Most Popular
                  </div>
                )}

                <div className="flex items-center gap-2.5">
                  <span className="h-9 w-9 rounded-lg bg-zinc-800/70 border border-zinc-800 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-amber-400" />
                  </span>
                  <div className="text-sm font-semibold text-zinc-50">{plan.name}</div>
                </div>

                <div className="mt-5">
                  <div className="text-3xl font-bold text-zinc-50 tracking-tight">
                    {isEnterprise ? "Coming Soon" : plan.price_display}
                  </div>
                  {isEnterprise ? (
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Indicative future pricing from $199/month
                    </div>
                  ) : (
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
                      {plan.beta_free ? "Free during beta" : CADENCE_LABEL[plan.cadence] || ""}
                    </div>
                  )}
                </div>

                {plan.audience && (
                  <div className="text-xs text-zinc-400 mt-4 leading-relaxed">
                    {isEnterprise ? "For universities, recruiters, and organizations." : plan.audience}
                  </div>
                )}
                {plan.tagline && !plan.audience && (
                  <div className="text-xs text-zinc-500 mt-4 leading-relaxed">
                    {plan.tagline}
                  </div>
                )}

                {isEnterprise && (
                  <div className="mt-5 text-[11px] uppercase tracking-widest text-zinc-500">
                    Planned Enterprise Features
                  </div>
                )}
                <ul className={`${isEnterprise ? "mt-2.5" : "mt-5"} space-y-2.5 flex-1`}>
                  {(isEnterprise
                    ? ["Everything in Career Pro", "Team Management", "Analytics Dashboard", "Dedicated Onboarding"]
                    : plan.features
                  ).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-relaxed">
                      <span
                        className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          highlight
                            ? "bg-amber-500/20 border border-amber-500/40"
                            : "bg-zinc-800 border border-zinc-700"
                        }`}
                      >
                        <Check className={`h-2.5 w-2.5 ${highlight ? "text-amber-400" : "text-zinc-400"}`} strokeWidth={3} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isEnterprise && (
                  <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                    Enterprise features are under development and are not currently available.
                  </p>
                )}

                <div className="mt-6">
                  {plan.cta_kind === "contact" ? (
                    <a
                      href={`mailto:${salesEmail}?subject=Intuiterview%20Enterprise%20inquiry`}
                      data-testid={`pricing-cta-${plan.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 text-sm bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2.5 transition-colors"
                    >
                      {plan.cta_label} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={primaryTarget}
                      data-testid={`pricing-cta-${plan.id}`}
                      className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors ${
                        highlight
                          ? "bg-amber-500 hover:bg-amber-400 text-amber-950 shadow-[0_0_20px_-6px_rgba(245,158,11,0.6)]"
                          : "border border-zinc-800 text-zinc-100 hover:bg-zinc-900"
                      }`}
                    >
                      {plan.cta_label}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p data-testid="pricing-page-beta-note" className="mt-8 text-center text-sm text-zinc-400">
          Intuiterview is currently in beta. All plans are free during the beta period.
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-zinc-500">
          <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30">
            <div className="text-zinc-200 text-sm font-semibold mb-1">No auto-charges on Free</div>
            You can use Intuiterview Free without ever entering a card.
          </div>
          <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30">
            <div className="text-zinc-200 text-sm font-semibold mb-1">Cancel anytime</div>
            Manage your subscription any time from Billing → Manage Subscription.
          </div>
          <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30">
            <div className="text-zinc-200 text-sm font-semibold mb-1">Your data stays yours</div>
            Delete your interview history or account whenever you like.
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-8 py-6 flex flex-wrap gap-4 justify-between text-xs text-zinc-600">
          <span>© 2026 Intuiterview</span>
          <div className="flex flex-wrap gap-4">
            <Link to="/trust" className="hover:text-zinc-300">Trust &amp; Privacy</Link>
            <Link to="/trust/terms" className="hover:text-zinc-300">Terms</Link>
            <Link to="/trust/privacy" className="hover:text-zinc-300">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
